import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "../Button/Button";
import Checkbox from "../Checkbox/Checkbox";
import Input from "../Input/Input";
import Select from "../Select/Select";
import ToggleButton from "../ToggleButton/ToggleButton";

import "./RfReceiverForm.css";

const RfReceiverForm = ({
	subModules,
	setSubModules,
	formMode,
	accessType,
	subModuleIndex,
}) => {
	const [activeTab, setActiveTab] = useState(subModuleIndex || 0);
	const [showAdvanced, setShowAdvanced] = useState(false);

	const { t } = useTranslation();

	const daysOfWeek = [
		{ value: "Sunday", label: t("common.sunday") },
		{ value: "Monday", label: t("common.monday") },
		{ value: "Tuesday", label: t("common.tuesday") },
		{ value: "Wednesday", label: t("common.wednesday") },
		{ value: "Thursday", label: t("common.thursday") },
		{ value: "Friday", label: t("common.friday") },
		{ value: "Saturday", label: t("common.saturday") },
	];

	const handleInputChange = useCallback(
		(index, field, value) => {
			setSubModules((prevSubModules) => {
				const updatedSubModules = [...prevSubModules];

				if (!updatedSubModules[index]) {
					updatedSubModules[index] = {
						subId: index + 1,
						name: "",
						enabled: false,
						mode: 0,
						pulseTime: 0,
						pulseCounter: 0,
						outputs: 0,
						relay1: false,
						relay2: false,
						relay3: false,
						relay4: false,
						alarmEnabled: false,
						alarmTimeout: 0,
						alarmPeriod: 0,
						alarmScheduled: false,
						alarmStartTime: null,
						alarmEndTime: null,
						alarmWeek: [],
						alarmWeekFull: false,
						uuid: null,
						autolock: false,
						closeDelay: 0,
						monitor: false,
						vehicle: false,
						vehicleUnknownAllowed: false,
						direction: 0,
						readDelay: 0,
						panicEnabled: false,
						sector: "",
					};
				}

				updatedSubModules[index][field] = value;

				return updatedSubModules;
			});
		},
		[setSubModules],
	);

	useEffect(() => {
		setActiveTab(subModuleIndex);
	}, [subModuleIndex]);

	useEffect(() => {
		if (formMode === "consulta") {
			setShowAdvanced(false);
		}
	}, [formMode]);

	const handleDayChange = (day, isEnabled) => {
		const updatedAlarmWeek = subModules?.[activeTab]?.alarmWeek || [];

		const newAlarmWeek = isEnabled
			? [...updatedAlarmWeek, day]
			: updatedAlarmWeek.filter((d) => d !== day);

		handleInputChange(activeTab, "alarmWeek", newAlarmWeek);
	};

	return (
		<div className="rf-receiver-form">
			{/* Abas */}
			<div className="tabs">
				{[
					t("equipment.buttonA"),
					t("equipment.buttonB"),
					t("equipment.buttonC"),
					t("equipment.buttonD"),
				].map((tab, index) => (
					<Button
						type="button"
						label={tab}
						key={index}
						className={`tab-button ${activeTab === index ? "active" : ""
							}`}
						onClick={() => setActiveTab(index)}
					>
						{tab}
					</Button>
				))}
			</div>

			<div className="tab-content">
				<div className="tab-content-name-and-toggle">
					<div className="group-of-inputs-access-container">
						<Input
							name={`name_${activeTab}`}
							id={`name_${activeTab}`}
							labelText={t("common.name")}
							value={subModules?.[activeTab]?.name}
							onChange={({ target }) =>
								handleInputChange(activeTab, "name", target.value)
							}
							disabled={formMode === "consulta"}
							required={true}
							tooltipVisible={formMode !== "consulta"}
							tooltipText={t("equipment.accessNameTooltip")}
						/>

						<Select
							name={`mode_${activeTab}`}
							id={`mode_${activeTab}`}
							labelText={t("equipment.mode")}
							value={subModules?.[activeTab]?.mode}
							onChange={({ target }) =>
								handleInputChange(activeTab, "mode", target.value)
							}
							disabled={formMode === "consulta"}
							required={true}
							options={[
								{
									value: 1,
									text: t("equipment.pulse"),
								},
								{
									value: 2,
									text: t("equipment.toggle"),
								},
							]}
							tooltipVisible={formMode !== "consulta"}
							tooltipText={t("equipment.modeTooltip")}
						/>

						<Input
							name={`time_${activeTab}`}
							id={`time_${activeTab}`}
							labelText={t("common.time")}
							type="number"
							value={subModules?.[activeTab]?.pulseTime}
							onChange={({ target }) =>
								handleInputChange(
									activeTab,
									"pulseTime",
									target.value,
								)
							}
							disabled={formMode === "consulta"}
							tooltipVisible={formMode !== "consulta"}
							tooltipText={t("equipment.relayPulseTimeTooltip")}
						/>

						<Input
							name={`quantity_${activeTab}`}
							id={`quantity_${activeTab}`}
							labelText={t("equipment.quantity")}
							type="number"
							value={subModules?.[activeTab]?.pulseCounter}
							onChange={({ target }) =>
								handleInputChange(
									activeTab,
									"pulseCounter",
									target.value,
								)
							}
							disabled={formMode === "consulta"}
							tooltipVisible={formMode !== "consulta"}
							tooltipText={t("equipment.quantityTooltip")}
						/>
					</div>

					<div className="group-toggle-button">
						<ToggleButton
							name={`enabled_${activeTab}`}
							id={`enabled_${activeTab}`}
							label={
								subModules?.[activeTab]?.enabled
									? t("common.enabled")
									: t("common.disabled")
							}
							defaultChecked={subModules?.[activeTab]?.enabled}
							selected={subModules?.[activeTab]?.enabled}
							onChange={({ target }) =>
								handleInputChange(
									activeTab,
									"enabled",
									target.checked,
								)
							}
							required={false}
							hidden={formMode === "consulta"}
							disabled={formMode === "consulta"}
							tooltipText={t("equipment.enableAccessTooltip")}
							tooltipVisible={true}
						/>

						<ToggleButton
							name={`car_access_${activeTab}`}
							id={`car_access_${activeTab}`}
							label={t("equipment.vehicleAccess")}
							defaultChecked={
								subModules?.[activeTab]?.vehicle || false
							}
							selected={subModules?.[activeTab]?.vehicle || false}
							onChange={({ target }) =>
								handleInputChange(
									activeTab,
									"vehicle",
									target.checked,
								)
							}
							required={false}
							disabled={formMode === "consulta"}
							tooltipText={t("equipment.vehicleAccessTooltip")}
							tooltipVisible={true}
							hidden={formMode === "consulta"}
						/>

						<ToggleButton
							name="car_unknown_allowed_access"
							id="car_unknown_allowed_access"
							label={t("equipment.unlinkedVehicleAccess")}
							defaultChecked={
								subModules?.[activeTab]?.vehicleUnknownAllowed ||
								false
							}
							selected={
								subModules?.[activeTab]?.vehicleUnknownAllowed ||
								false
							}
							onChange={({ target }) =>
								handleInputChange(
									activeTab,
									"vehicleUnknownAllowed",
									target.checked,
								)
							}
							required={false}
							disabled={formMode === "consulta"}
							tooltipText={t(
								"equipment.unlinkedVehicleAccessTooltip",
							)}
							tooltipVisible={true}
							hidden={formMode === "consulta"}
						/>

						<ToggleButton
							name={`direction_${activeTab}`}
							id={`direction_${activeTab}`}
							label={t("equipment.exitDirection")}
							defaultChecked={
								subModules?.[activeTab]?.direction || false
							}
							selected={
								subModules?.[activeTab]?.direction || false
							}
							onChange={({ target }) =>
								handleInputChange(
									activeTab,
									"direction",
									target.checked,
								)
							}
							required={false}
							disabled={formMode === "consulta"}
							tooltipText={t("equipment.exitDirection")}
							tooltipVisible={true}
							hidden={formMode === "consulta"}
						/>

						<ToggleButton
							name={`visible_on_monitoring_${activeTab}`}
							id={`visible_on_monitoring_${activeTab}`}
							label={t("equipment.visibleOnMonitoring")}
							defaultChecked={
								subModules?.[activeTab]?.monitor || false
							}
							selected={
								subModules?.[activeTab]?.monitor || false
							}
							onChange={({ target }) =>
								handleInputChange(
									activeTab,
									"monitor",
									target.checked,
								)
							}
							required={false}
							disabled={formMode === "consulta"}
							tooltipText={t(
								"equipment.enableMonitoringVisibilityTooltip",
							)}
							tooltipVisible={true}
							hidden={formMode === "consulta"}
						/>
					</div>
				</div>

				{accessType === 2 && (
					<div className="group-of-checkboxes-access-container-rf">
						<div className="group-of-inputs-container">
							<p className="group-of-inputs-title">
								{t("equipment.outputs")}
							</p>

							{[1, 2, 3, 4].map((relay) => (
								<Checkbox
									key={relay}
									labelText={`Relay ${relay}`}
									name={`relay${relay}`}
									id={`relay${relay}`}
									minWidthLabel="130px"
									defaultChecked={
										subModules?.[activeTab]?.[`relay${relay}`] ||
										false
									}
									checked={
										subModules?.[activeTab]?.[`relay${relay}`] ||
										false
									}
									onChange={({ target }) =>
										handleInputChange(
											activeTab,
											`relay${relay}`,
											target.checked,
										)
									}
									required={false}
									disabled={formMode === "consulta"}
								/>
							))}
						</div>
					</div>
				)}

				{formMode !== "consulta" && (
					<div className="advanced-settings-checkbox-rf">
						<Checkbox
							labelText={t("equipment.advancedSettings")}
							checked={showAdvanced}
							minWidthLabel="170px"
							highlighted={true}
							onChange={({ target }) =>
								setShowAdvanced(target.checked)
							}
							required={false}
							disabled={formMode === "consulta"}
						/>
					</div>
				)}

				{showAdvanced && (
					<div className="advanced-container">
						<div className="container-advanced-settings">
							<div className="group-of-inputs-advanced-container">
								<Input
									name={`alarmTimeout_${activeTab}`}
									id={`alarmTimeout_${activeTab}`}
									labelText={t("equipment.openTime")}
									type="number"
									value={
										subModules?.[activeTab]?.alarmTimeout
									}
									onChange={({ target }) =>
										handleInputChange(
											activeTab,
											"alarmTimeout",
											target.value,
										)
									}
									disabled={
										formMode === "consulta" ||
										!subModules?.[activeTab]?.alarmEnabled
									}
									required={false}
								/>

								<Input
									name={`alarm_period_${activeTab}`}
									id={`alarm_period_${activeTab}`}
									labelText={t("equipment.alarmPeriod")}
									type="number"
									value={
										subModules?.[activeTab]?.alarmPeriod
									}
									onChange={({ target }) =>
										handleInputChange(
											activeTab,
											"alarmPeriod",
											target.value,
										)
									}
									disabled={
										formMode === "consulta" ||
										!subModules?.[activeTab]?.alarmEnabled
									}
									required={false}
								/>
							</div>

							<div className="group-of-checkboxes-advanced-container">
								<ToggleButton
									label={t("equipment.enableOpenDoorAlarm")}
									name={`alarm_port_open_${activeTab}`}
									id={`alarm_port_open_${activeTab}`}
									minWidthLabel="170px"
									defaultChecked={
										subModules?.[activeTab]?.alarmEnabled
									}
									selected={
										subModules?.[activeTab]?.alarmEnabled
									}
									onChange={({ target }) =>
										handleInputChange(
											activeTab,
											"alarmEnabled",
											target.checked,
										)
									}
									required={false}
									disabled={formMode === "consulta"}
								/>

								<ToggleButton
									label={t("equipment.enableAlarmCalendar")}
									name={`alarm_calendar_${activeTab}`}
									id={`alarm_calendar_${activeTab}`}
									minWidthLabel="170px"
									defaultChecked={
										subModules?.[activeTab]?.alarmScheduled
									}
									selected={
										subModules?.[activeTab]?.alarmScheduled
									}
									onChange={({ target }) =>
										handleInputChange(
											activeTab,
											"alarmScheduled",
											target.checked,
										)
									}
									required={false}
									disabled={formMode === "consulta"}
								/>
							</div>
						</div>

						<div
							className="container-advanced-date-time"
							style={{
								opacity:
									subModules?.[activeTab]?.alarmScheduled &&
										subModules?.[activeTab]?.alarmEnabled
										? "1"
										: "0.3",
								pointerEvents:
									subModules?.[activeTab]?.alarmScheduled &&
										subModules?.[activeTab]?.alarmEnabled
										? "auto"
										: "none",
								cursor:
									subModules?.[activeTab]?.alarmScheduled &&
										subModules?.[activeTab]?.alarmEnabled
										? "pointer"
										: "not-allowed",
							}}
						>
							<div className="date-time-group-access">
								<div className="date-time-container">
									<h3 className="box-title">
										{t("equipment.timeAndDates")}
									</h3>

									<div className="date-time-grid">
										<Input
											name={`start_hour_${activeTab}`}
											id={`start_hour_${activeTab}`}
											type="time"
											min="00:00"
											max="23:59"
											labelText={t("common.startTime")}
											value={
												subModules?.[activeTab]?.alarmStartTime
											}
											onChange={({ target }) =>
												handleInputChange(
													activeTab,
													"alarmStartTime",
													target.value,
												)
											}
											disabled={
												formMode === "consulta" ||
												!(
													subModules?.[activeTab]
														?.alarmScheduled &&
													subModules?.[activeTab]
														?.alarmEnabled
												)
											}
											required={false}
											tooltipText={t(
												"equipment.accessGroupStartTimeTooltip",
											)}
											tooltipVisible={true}
										/>

										<Input
											name="end_hour"
											id="end_hour"
											type="time"
											min="00:00"
											max="23:59"
											labelText={t("common.endTime")}
											value={
												subModules?.[activeTab]?.alarmEndTime
											}
											onChange={({ target }) =>
												handleInputChange(
													activeTab,
													"alarmEndTime",
													target.value,
												)
											}
											disabled={
												formMode === "consulta" ||
												!(
													subModules?.[activeTab]
														?.alarmScheduled &&
													subModules?.[activeTab]
														?.alarmEnabled
												)
											}
											required={false}
											tooltipText={t(
												"equipment.accessGroupEndTimeTooltip",
											)}
											tooltipVisible={true}
										/>
									</div>

									<div className="container-interval-checkbox">
										<Checkbox
											labelText={t("common.continuousInterval")}
											name={`continuous_interval_${activeTab}`}
											id={`continuous_interval_${activeTab}`}
											defaultChecked={
												subModules?.[activeTab]?.alarmWeekFull
											}
											checked={
												subModules?.[activeTab]?.alarmWeekFull
											}
											onChange={({ target }) =>
												handleInputChange(
													activeTab,
													"alarmWeekFull",
													target.checked,
												)
											}
											required={false}
											inputWidth="200px"
											minWidthLabel="120px"
											disabled={
												formMode === "consulta" ||
												!(
													subModules?.[activeTab]
														?.alarmScheduled &&
													subModules?.[activeTab]
														?.alarmEnabled
												)
											}
										/>
									</div>
								</div>
							</div>

							<div className="group-of-inputs-date-container">
								<p className="group-of-inputs-title">
									{t("equipment.activeWeekdays")}
								</p>

								<div className="checkbox-container">
									{daysOfWeek.map((day) => (
										<Checkbox
											key={day.value}
											labelText={day.label}
											checked={
												subModules?.[
													activeTab
												]?.alarmWeek?.includes(day.value) || false
											}
											onChange={({ target }) =>
												handleDayChange(
													day.value,
													target.checked,
												)
											}
											required={false}
											minWidthLabel="120px"
											disabled={
												formMode === "consulta" ||
												!(
													subModules?.[activeTab]
														?.alarmScheduled &&
													subModules?.[activeTab]
														?.alarmEnabled
												)
											}
										/>
									))}
								</div>
							</div>
						</div>

						<div className="container-footer-config-rf">
							<div className="group-of-inputs-footer-config-rf">
								<Input
									name="delay_closing_sensor"
									id="delay_closing_sensor"
									type="number"
									labelText={t(
										"equipment.closingSensorDelay",
									)}
									value={
										subModules?.[activeTab]?.closeDelay
									}
									onChange={({ target }) =>
										handleInputChange(
											activeTab,
											"closeDelay",
											target.value,
										)
									}
									disabled={formMode === "consulta"}
									required={false}
									tooltipText={t(
										"equipment.closingSensorDelayTooltip",
									)}
									tooltipVisible={true}
								/>

								<Input
									name={`sector_${activeTab}`}
									id={`sector_${activeTab}`}
									type="text"
									labelText={t("equipment.sector")}
									value={
										subModules?.[activeTab]?.sector
									}
									onChange={({ target }) =>
										handleInputChange(
											activeTab,
											"sector",
											target.value,
										)
									}
									disabled={formMode === "consulta"}
									required={false}
									tooltipText={t("equipment.sectorTooltip")}
									tooltipVisible={true}
								/>

								<Input
									name={`uuid_${activeTab}`}
									id={`uuid_${activeTab}`}
									labelText="UUID"
									value={
										subModules?.[activeTab]?.uuid
									}
									onChange={({ target }) =>
										handleInputChange(
											activeTab,
											"uuid",
											target.value,
										)
									}
									tooltipText={t("equipment.uuidTooltip")}
									tooltipVisible={true}
								/>
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default RfReceiverForm;